import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { MenuItem, TextField } from '@mui/material'
import React from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import type { EstadoCivil, Genero, TipoPersona } from '../../../model/kartera-api-model'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import { useProspectFormModel } from './prospect-form-model'
import type { ProspectFormProps } from './prospect-form-model'

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }

const ProspectForm = (props: ProspectFormProps) => {
    const model = useProspectFormModel(props)
    const { form, change } = model

    return (
        <KarteraAdminTemplate loading={false} modal={props.modal} title={model.title}>
            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.loading && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    description={model.isEdit ? 'Actualiza los datos del prospecto en tu cartera.' : 'Captura rápida: solo los datos necesarios para registrarlo como prospecto.'}
                    backTo={model.backTo}
                    backLabel="Prospectos"
                    actions={<span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Datos generales</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-3">
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth
                            select
                            required
                            label="Personalidad"
                            value={form.personalidad}
                            helperText="Persona física o moral."
                            onChange={(event) => change.personalidad(event.target.value as TipoPersona)}
                            slotProps={{ select: selectMenu }}
                        >
                            <MenuItem value="fisica">Física</MenuItem>
                            <MenuItem value="moral">Moral</MenuItem>
                        </TextField>
                    </div>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-3">
                        {model.esFisica ? (
                            <>
                                <TextField
                                    className="v2-field md:col-span-3" variant="filled" fullWidth
                                    required
                                    label="Nombre"
                                    value={form.nombres}
                                    onChange={(event) => change.nombres(event.target.value)}
                                />
                                <TextField
                                    className="v2-field" variant="filled" fullWidth
                                    label="Nacimiento"
                                    type="date"
                                    value={model.fecNacimientoValue}
                                    onChange={(event) => change.fecNacimiento(event.target.value)}
                                />
                                <TextField
                                    className="v2-field v2-select" variant="filled" fullWidth
                                    select
                                    label="Sexo"
                                    value={form.sexo}
                                    onChange={(event) => change.sexo(event.target.value as Genero | '')}
                                    slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                                >
                                    <MenuItem value=""><em>Seleccione</em></MenuItem>
                                    <MenuItem value="hombre">Hombre</MenuItem>
                                    <MenuItem value="mujer">Mujer</MenuItem>
                                </TextField>
                                <TextField
                                    className="v2-field v2-select" variant="filled" fullWidth
                                    select
                                    label="Estado civil"
                                    value={form.edoCivil}
                                    onChange={(event) => change.edoCivil(event.target.value as EstadoCivil | '')}
                                    slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                                >
                                    <MenuItem value=""><em>Seleccione</em></MenuItem>
                                    <MenuItem value="soltero">Soltero</MenuItem>
                                    <MenuItem value="casado">Casado</MenuItem>
                                    <MenuItem value="viudo">Viudo</MenuItem>
                                    <MenuItem value="divorciado">Divorciado</MenuItem>
                                    <MenuItem value="concubinato">Concubinato</MenuItem>
                                </TextField>
                            </>
                        ) : (
                            <TextField
                                className="v2-field md:col-span-3" variant="filled" fullWidth
                                required
                                label="Razón social"
                                value={form.nombres}
                                onChange={(event) => change.nombres(event.target.value)}
                            />
                        )}
                    </div>
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">Localización</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-3">
                        <TextField
                            className="v2-field md:col-span-2" variant="filled" fullWidth
                            type="email"
                            label="Correo"
                            value={form.email}
                            onChange={(event) => change.email(event.target.value)}
                        />
                        <div className="v2-phone">
                            <span className="v2-phone-label">Teléfono</span>
                            <PhoneInput
                                country="mx"
                                preferredCountries={['mx', 'us']}
                                disableDropdown
                                specialLabel=""
                                value={form.telefono}
                                onChange={(value) => change.telefono(value)}
                                inputProps={{ name: 'telefono' }}
                            />
                            <p className="v2-phone-hint">Número con el que se localiza al prospecto.</p>
                        </div>
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button
                        type="button"
                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={model.loading}
                        className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <CheckIcon />
                        Guardar prospecto
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default ProspectForm
